export function extractCompositeGameJobId(): string | null {
  if (typeof window === "undefined") return null;
  const { href, search, hash } = window.location;

  const urlParams = new URLSearchParams(search);
  const gameIdParam = urlParams.get("gameId") || urlParams.get("compositeGameJobId");
  if (gameIdParam) return gameIdParam;

  const gamePathMatch = href.match(/\/games\/game\/([0-9]{2}-[0-9]{2}-[0-9]{2}\/gjb_[0-9]+)/);
  if (gamePathMatch) return gamePathMatch[1];

  const pathMatch = href.match(/\/games\/([0-9]{2}-[0-9]{2}-[0-9]{2}\/gjb_[0-9]+)/);
  if (pathMatch) return pathMatch[1];

  const hashMatch = hash.match(/#\/game\/([0-9]{2}-[0-9]{2}-[0-9]{2}\/gjb_[0-9]+)/);
  if (hashMatch) return hashMatch[1];

  return null;
}

export function extractVoiceIdAndToken(): { voiceId: string | null; token: string | null } {
  if (typeof window === "undefined") {
    return { voiceId: null, token: null };
  }

  const params = new URLSearchParams(window.location.search);
  return {
    voiceId: params.get("voiceId"),
    token: params.get("token"),
  };
}

export function extractUrlParameters(): {
  voiceId: string | null;
  token: string | null;
  set: string | null;
  level: string | null;
  question: string | null;
  gameId: string | null;
  compositeGameJobId: string | null;
} {
  if (typeof window === "undefined") {
    return {
      voiceId: null,
      token: null,
      set: null,
      level: null,
      question: null,
      gameId: null,
      compositeGameJobId: null,
    };
  }

  const params = new URLSearchParams(window.location.search);
  return {
    voiceId: params.get("voiceId"),
    token: params.get("token"),
    set: params.get("set"),
    level: params.get("level"),
    question: params.get("question"),
    gameId: params.get("gameId"),
    compositeGameJobId: params.get("compositeGameJobId"),
  };
}

export const getAudioStorageKey = (id: string | null) => (id ? `kinzy_audio_urls_${id}` : null);

export const getAudioCacheKey = (text: string) => text.trim().toLowerCase();

export const getMuteStorageKey = (id: string | null) => (id ? `kinzy_audio_muted_${id}` : null);

import { SCORING } from './constants';

export const calculateLevelScore = (correctAnswers: number): number => {
  return correctAnswers * SCORING.POINTS_PER_QUESTION;
};

export const calculateTotalPossible = (totalQuestions: number): number => {
  return totalQuestions * SCORING.POINTS_PER_QUESTION;
};

export const calculateStarsEarned = (correctAnswers: number, totalQuestions: number): number => {
  const percentage = correctAnswers / totalQuestions;

  if (percentage >= SCORING.STAR_THRESHOLDS.THREE_STARS) return 3;
  if (percentage >= SCORING.STAR_THRESHOLDS.TWO_STARS) return 2;
  if (percentage >= SCORING.STAR_THRESHOLDS.ONE_STAR) return 1;
  return 0;
};

export const isLevelUnlocked = (correctAnswers: number, totalQuestions: number): boolean => {
  const percentage = correctAnswers / totalQuestions;
  return percentage >= SCORING.LEVEL_UNLOCK_THRESHOLD;
};

export const getPerformanceMessage = (starsEarned: number): string => {
  switch (starsEarned) {
    case 3: return "Outstanding! 🌟";
    case 2: return "Great job! 👏";
    case 1: return "Good effort! 👍";
    default: return "Keep practicing! 💪";
  }
};

export type VoiceIdAndToken = {
  voiceId: string | null;
  token: string | null;
};

export type UrlParameters = {
  voiceId: string | null;
  token: string | null;
  set: string | null;
  level: string | null;
  question: string | null;
  gameId: string | null;
  compositeGameJobId: string | null;
};

export interface ScoreCalculation {
  score: number;
  totalPossible: number;
  starsEarned: number;
  isUnlocked: boolean;
  performanceMessage: string;
}

export const calculateLevelResults = (
  correctAnswers: number,
  totalQuestions: number
): ScoreCalculation => {
  const score = calculateLevelScore(correctAnswers);
  const totalPossible = calculateTotalPossible(totalQuestions);
  const starsEarned = calculateStarsEarned(correctAnswers, totalQuestions);
  const isUnlocked = isLevelUnlocked(correctAnswers, totalQuestions);
  const performanceMessage = getPerformanceMessage(starsEarned);

  return {
    score,
    totalPossible,
    starsEarned,
    isUnlocked,
    performanceMessage
  };
};